import { CalendarDays, CircleDot } from "lucide-react";
import { Pill } from "@/components/primitives/pill";
import type { TenantLocation } from "@/lib/mock-data";

export interface ContextBarProps {
  location: TenantLocation;
  service: string;
  dateLabel: string;
  operatingState: "open" | "prepping" | "closed";
}

const OPERATING_LABEL: Record<ContextBarProps["operatingState"], string> = {
  open: "Service open",
  prepping: "Prepping for service",
  closed: "Closed",
};

const OPERATING_TONE: Record<ContextBarProps["operatingState"], "success" | "warning" | "neutral"> = {
  open: "success",
  prepping: "warning",
  closed: "neutral",
};

/**
 * Context Bar — shows current restaurant, service, date and operating state
 * (docs/COMPONENT_LIBRARY.md, verbatim scope).
 */
export function ContextBar({ location, service, dateLabel, operatingState }: ContextBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border-subtle bg-surface-secondary px-4 py-2.5 sm:px-6">
      <p className="text-xs font-bold text-text-primary">
        {location.tenantName} <span className="font-normal text-text-secondary">/ {location.city}</span>
      </p>
      <span className="hidden h-3.5 w-px bg-border-strong sm:block" aria-hidden="true" />
      <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
        <CircleDot aria-hidden="true" size={13} />
        {service}
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
        <CalendarDays aria-hidden="true" size={13} />
        {dateLabel}
      </span>
      <Pill tone={OPERATING_TONE[operatingState]} className="ms-auto">
        {OPERATING_LABEL[operatingState]}
      </Pill>
    </div>
  );
}
